/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react'

import {
  ComponentStyles,
  NestedComponentProvider,
  StyleProvider,
  StylingSegment,
  StylingSegmentFull,
} from '@pijma/hero-types'

import { css } from '@emotion/css'

import { useTheme } from './'

const hasNoTheme =
  'There is no theme in ThemeContext. Please wrap your app in BlackHoleProvider and specify BlackHoleProvider.theme'
const hasNoComponentStyles =
  'There is no componentStyles in ThemeContext. Please wrap your app in BlackHoleProvider and specify BlackHoleProvider.componentStyles'

type ComponentKeys = keyof ComponentStyles<{}>

function hasKey<T extends object>(obj: T, key: keyof any): key is keyof T {
  return key in obj
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useStyles<
  T extends StylingSegment<any, any>,
  TNestedKey = undefined,
>(
  path: TNestedKey extends string
    ? [ComponentKeys, TNestedKey]
    : [ComponentKeys],
  props: T extends StylingSegment<infer TProps, any> ? TProps : unknown,
  options?: { defaultStyles?: T['styleProvider'] },
): {
  classNames: Record<keyof T['styleProvider'], string>
  components: T extends StylingSegmentFull<any, any, any>
    ? Record<keyof T['components'], ReactNode>
    : undefined
} {
  const themeContext = useTheme()
  if (!themeContext?.theme) throw new Error(hasNoTheme)
  if (!themeContext?.componentStyles) throw new Error(hasNoComponentStyles)

  const componentStyle = themeContext.componentStyles[path[0]]
  if (!componentStyle) throw new Error(`There are no Styles for ${path}`)

  const segment = componentStyle(themeContext.theme.tokens)
  const { styleProvider, components } =
    path[1] && segment && hasKey(segment, path[1])
      ? (segment[path[1]] as StylingSegmentFull<any, any, any>)
      : (segment as StylingSegmentFull<any, any, any>)

  // "important" styles
  let importantStyleProvider: Record<any, StyleProvider<any>>,
    importantComponents: Record<any, NestedComponentProvider<any>>
  const importantSegment = themeContext.important?.[path[0]]
  if (importantSegment) {
    ;({
      styleProvider: importantStyleProvider,
      components: importantComponents,
    } =
      path[1] && hasKey(importantSegment, path[1])
        ? (importantSegment[path[1]] as StylingSegmentFull<any, any, any>)
        : (importantSegment as StylingSegmentFull<any, any, any>))
  }
  // "important" styles

  const classNames = Object.fromEntries(
    Object.entries(styleProvider).map(([key, value]) => {
      const defaultStyleFn = options?.defaultStyles?.[key]
      const defaultStyles =
        typeof defaultStyleFn === 'function' ? defaultStyleFn(props) : {}

      const regularStylesFromThemeContext =
        typeof value === 'function' ? value(props) : {}

      // "important" styles
      const importantStylesFromThemeContext =
        typeof importantStyleProvider?.[key] === 'function'
          ? importantStyleProvider?.[key](props)
          : undefined
      // "important" styles

      return [
        key,
        css({
          ...defaultStyles,
          ...regularStylesFromThemeContext,
          ...importantStylesFromThemeContext,
        }),
      ]
    }),
  )

  const componentNodes = components
    ? Object.fromEntries(
        Object.entries(components).map(([key, value]) => {
          const regularComponentsFromThemeContext =
            typeof value === 'function' ? value(props) : null

          // "important" styles
          const importantComponentsFromThemeContext =
            typeof importantComponents?.[key] === 'function'
              ? importantComponents?.[key](props)
              : undefined
          // "important" styles

          return [
            key,
            importantComponentsFromThemeContext === undefined
              ? regularComponentsFromThemeContext
              : importantComponentsFromThemeContext,
          ]
        }),
      )
    : undefined

  return {
    classNames: classNames as Record<keyof T['styleProvider'], string>,
    components: componentNodes as T extends StylingSegmentFull<any, any, any>
      ? Record<keyof T['components'], ReactNode>
      : undefined,
  }
}
